import { RenderTargetTexture as BABYLON_RenderTargetTexture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/renderTargetTexture.js';
import { RenderingGroup as BABYLON_RenderingGroup } from '@inter-ikea-betrakta-babylonjs/core/Rendering/renderingGroup.js';
import { Constants as BABYLON_Constants } from '@inter-ikea-betrakta-babylonjs/core/Engines/constants.js';
import { Texture as BABYLON_Texture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/texture.js';
import { IVector3 } from '../../api/IVector3.js';
/**
 * This is an extension of Babylon' render target texture to provide some more lightweight rendering functionality.
 * It implements a function called "render_lite" which is a lighter version of just calling "render" on the render target,
 * and all it does is render the list of meshes you gave it.
 * No frustum culling or layer mask checks are performed, but transparent meshes are sorted back-to-front.
 */
export class LiteRenderTarget extends BABYLON_RenderTargetTexture {
    constructor(name, size, scene, generate_mipmaps = false, do_not_change_aspect_ratio = true, type = BABYLON_Constants.TEXTURETYPE_UNSIGNED_INT, is_cube = false, sampling_mode = BABYLON_Texture.TRILINEAR_SAMPLINGMODE, generate_depth_buffer = true, generate_stencil_buffer = false, is_multi = false, format = BABYLON_Constants.TEXTUREFORMAT_RGBA, delay_allocation = false, samples, creation_flags, no_color_attachment = false, use_srgb_buffer = false) {
        super(name, size, scene, generate_mipmaps, do_not_change_aspect_ratio, type, is_cube, sampling_mode, generate_depth_buffer, generate_stencil_buffer, is_multi, format, delay_allocation, samples, creation_flags, no_color_attachment, use_srgb_buffer);
        this._camera = null;
        this._opaque_render_list_part = [];
        this._transparent_render_list_part = [];
        this.lite_custom_render_function = (opaque, alpha_test, transparent, depth_only) => {
            const engine = this._scene.getEngine();
            // Depth only
            if (depth_only.length !== 0) {
                engine.setColorWrite(false);
                this.render_sorted(depth_only, BABYLON_RenderingGroup.PainterSortCompare, false);
                engine.setColorWrite(true);
            }
            // Opaque
            if (opaque.length !== 0) {
                this.render_sorted(opaque, BABYLON_RenderingGroup.PainterSortCompare, false);
            }
            // Alpha test
            if (alpha_test.length !== 0) {
                this.render_sorted(alpha_test, BABYLON_RenderingGroup.PainterSortCompare, false);
            }
            // Transparent
            if (transparent.length > 0) {
                // No sorting function as we sorted earlier, as sorting on submesh level is not sufficient.
                this.render_sorted(transparent, null, true);
            }
        };
    }
    prepare_render_list(render_list) {
        const scene = this.getScene();
        const current_scene_render_id = scene.getRenderId();
        for (let i = 0; i < render_list.length; i++) {
            const mesh = render_list[i];
            if (mesh && !mesh.isBlocked) {
                if (this.customIsReadyFunction) {
                    if (!this.customIsReadyFunction(mesh, this.refreshRate, false)) {
                        this.resetRefreshCounter();
                        continue;
                    }
                }
                else if (!mesh.isReady(this.refreshRate === 0)) {
                    this.resetRefreshCounter();
                    continue;
                }
                if (!mesh._internalAbstractMeshDataInfo._currentLODIsUpToDate && scene.activeCamera) {
                    mesh._internalAbstractMeshDataInfo._currentLOD = scene.customLODSelector
                        ? scene.customLODSelector(mesh, this.activeCamera || scene.activeCamera)
                        : mesh.getLOD(this.activeCamera || scene.activeCamera);
                    mesh._internalAbstractMeshDataInfo._currentLODIsUpToDate = true;
                }
                if (!mesh._internalAbstractMeshDataInfo._currentLOD) {
                    continue;
                }
                let mesh_to_render = mesh._internalAbstractMeshDataInfo._currentLOD;
                mesh_to_render._preActivateForIntermediateRendering(current_scene_render_id);
                if (mesh.isEnabled() && mesh.isVisible && mesh.subMeshes) {
                    if (mesh_to_render !== mesh) {
                        mesh_to_render._activate(current_scene_render_id, true);
                    }
                    if (mesh._activate(current_scene_render_id, true) && mesh.subMeshes.length) {
                        if (!mesh.isAnInstance) {
                            mesh_to_render._internalAbstractMeshDataInfo._onlyForInstancesIntermediate = false;
                        }
                        else {
                            if (mesh._internalAbstractMeshDataInfo._actAsRegularMesh) {
                                mesh_to_render = mesh;
                            }
                        }
                        mesh_to_render._internalAbstractMeshDataInfo._isActiveIntermediate = true;
                        for (let sub_mesh_index = 0; sub_mesh_index < mesh_to_render.subMeshes.length; sub_mesh_index++) {
                            const sub_mesh = mesh_to_render.subMeshes[sub_mesh_index];
                            this._renderingManager.dispatch(sub_mesh, mesh_to_render);
                        }
                    }
                }
            }
        }
    }
    render_lite(camera, render_list) {
        var _a;
        this._camera = camera;
        // Split render list into opaque and transparent parts
        this._opaque_render_list_part.length = 0;
        this._transparent_render_list_part.length = 0;
        for (let i = 0; i < render_list.length; i++) {
            if ((_a = render_list[i].material) === null || _a === void 0 ? void 0 : _a.needAlphaBlending()) {
                this._transparent_render_list_part.push(render_list[i]);
            }
            else {
                this._opaque_render_list_part.push(render_list[i]);
            }
        }
        const view_z = camera.forward;
        for (let i = 0; i < this._transparent_render_list_part.length; i++) {
            this._transparent_render_list_part[i]['_viewer_lrt_distance_to_camera'] = IVector3.dot(view_z, this._transparent_render_list_part[i].getBoundingInfo().boundingSphere.centerWorld);
        }
        // We have to sort our transparent meshes before submitting to the rendering manager, otherwise we won't be able to sort our instances.
        this._transparent_render_list_part.sort((a, b) => {
            const k = Math.sign(a.alphaIndex - b.alphaIndex);
            return k + (1.0 - Math.abs(k)) * (b['_viewer_lrt_distance_to_camera'] - a['_viewer_lrt_distance_to_camera']);
        });
        this._renderingManager.reset();
        this.prepare_render_list(this._opaque_render_list_part);
        this.prepare_render_list(this._transparent_render_list_part);
        // @ts-ignore
        this._renderingManager.render(this.lite_custom_render_function, render_list, false, false);
        this._camera = null;
    }
    render_sorted(sub_meshes, sort_function, is_transparent) {
        const sorted_meshes = sub_meshes.length === sub_meshes.data.length ? sub_meshes.data : sub_meshes.data.slice(0, sub_meshes.length);
        if (sort_function) {
            sorted_meshes.sort(sort_function);
        }
        for (let i = 0; i < sorted_meshes.length; i++) {
            const sub_mesh = sorted_meshes[i];
            if (is_transparent) {
                const material = sub_mesh.getMaterial();
                if (material && material.needDepthPrePass) {
                    const engine = material.getScene().getEngine();
                    engine.setColorWrite(false);
                    engine.setAlphaMode(BABYLON_Constants.ALPHA_DISABLE);
                    sub_mesh.render(false);
                    engine.setColorWrite(true);
                }
            }
            sub_mesh.render(is_transparent);
        }
    }
}
//# sourceMappingURL=LiteRenderTarget.js.map